import React from 'react';
import draftingFlyer from '../assets/images/services/arch_floor_plan.png';
import systemDesignFlyer from '../assets/images/system_design.png';
import takeoffFlyer from '../assets/images/takeoff.png';
import blogCoverImage from '../assets/images/services/arch_floor_plan.png';

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: React.ReactNode;
  author: string;
  date: string;
  coverImage: string;
  metaTitle: string;
  metaDescription: string;
}

export const blogs: BlogPost[] = [
  {
    id: '1',
    slug: 'drafting-support-for-low-voltage-integrators',
    title: 'Drafting Support for Low-Voltage Integrators: Drafting, Design, Programming & Takeoffs',
    excerpt: 'Drafting by DMI provides drafting, system design, programming, takeoffs, estimating, and proposal support for low-voltage integrators.',
    author: 'DMI Team',
    date: 'September 19, 2026',
    coverImage: blogCoverImage,
    metaTitle: 'Low-Voltage Design & Drafting Support | Drafting by DMI',
    metaDescription: 'Drafting by DMI provides drafting, system design, programming, takeoffs, estimating, and proposal support for low-voltage integrators.',
    content: (
      <div className="space-y-8 text-gray-800 dark:text-gray-200">
        <p className="text-xl leading-relaxed text-gray-600 dark:text-gray-300 font-medium">
          A successful low-voltage project starts long before equipment reaches the job site.
        </p>

        <p className="leading-relaxed">
          Before a system can be installed, someone needs to interpret plans, develop system layouts, coordinate equipment, prepare drawings, build accurate takeoffs, organize proposals, and ensure that the design communicates the intent of the project clearly. For low-voltage integrators, that work can quickly become a bottleneck.
        </p>

        <p className="leading-relaxed">
          Engineering teams may be managing multiple active projects. Designers may be working through a growing backlog of drawings. Estimators may be racing to meet bid deadlines. Programming work may compete with installation and project management responsibilities.
        </p>

        <blockquote className="border-l-4 border-[#0d9488] pl-6 py-2 my-10 italic text-xl text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800/30 rounded-r-xl">
          That's where Drafting by DMI comes in. We provide design, drafting, programming, takeoff, and proposal support for low-voltage integrators—helping teams increase their capacity without having to build every capability in-house.
        </blockquote>

        <p className="leading-relaxed font-medium">
          Our goal is straightforward: give low-voltage teams the design and project support they need to keep projects moving.
        </p>

        <hr className="border-gray-200 dark:border-gray-800 my-10" />

        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-12 mb-6">One Partner for the Work Behind the Project</h3>

        <p className="leading-relaxed mb-6">
          Low-voltage projects often involve several interconnected stages. A project may begin with architectural plans and a scope of work. From there, the team needs to determine system requirements, develop layouts, create drawings, estimate quantities, prepare proposals, and eventually configure or program the systems.
        </p>

        <p className="leading-relaxed mb-6">
          Each step affects the next. A well-developed drawing can make installation easier. An accurate takeoff can make a proposal more reliable. A coordinated system design can reduce questions during installation. A properly structured programming plan can help move a system toward final commissioning.
        </p>

        <p className="leading-relaxed mb-10">
          Drafting by DMI supports these stages through three primary service areas:
        </p>

        {/* ── Section: Drafting & Design Support ── */}
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-6">Drafting & Design Support</h3>

        <figure className="my-10">
          <img src={draftingFlyer} alt="Drafting Support Flyer" className="w-full rounded-2xl shadow-md border border-gray-100 dark:border-gray-800" />
          <figcaption className="text-center text-sm text-gray-500 mt-3">Comprehensive AV, network, and security device placement layout.</figcaption>
        </figure>

        <p className="leading-relaxed mb-6">
          Drafting by DMI provides drafting support for residential and commercial low-voltage projects, including drawing packages for AV, security, networking, lighting, and integrated systems. Our drafting deliverables can include:
        </p>

        <ul className="list-disc pl-5 space-y-2 mb-8">
          <li>Prewire and device layout plans</li>
          <li>Line diagrams and system schematics</li>
          <li>Rack elevations and infrastructure layouts</li>
          <li>Wi-Fi coverage and network planning maps</li>
          <li>Camera, audio, and speaker layout plans</li>
          <li>Lighting analysis and illumination maps</li>
          <li>3D renderings and presentation visuals</li>
          <li>Coordination-ready drawing packages</li>
        </ul>

        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Why This Matters to Integrators</h4>
        <p className="leading-relaxed mb-6">
          When your internal team is overloaded, drafting can become one of the biggest constraints on project throughput. Bringing in additional drafting support allows engineers and project managers to focus on higher-value work while maintaining the documentation required for active projects.
        </p>
        <p className="leading-relaxed mb-10">
          The objective isn't simply to produce more drawings. It's to produce clear, organized, professional documentation that your team can use throughout the project lifecycle.
        </p>

        {/* ── Section: System Design & Programming ── */}
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-6">System Design & Programming</h3>

        <figure className="my-10">
          <img src={systemDesignFlyer} alt="System Design & Programming Flyer" className="w-full rounded-2xl shadow-md border border-gray-100 dark:border-gray-800" />
          <figcaption className="text-center text-sm text-gray-500 mt-3">System architecture and infrastructure coordination.</figcaption>
        </figure>

        <p className="leading-relaxed mb-6">
          Complex low-voltage projects also require decisions about how the system should actually work. What equipment is required? How should devices connect? Where should infrastructure be located? How should the system be organized? How should lighting scenes or automation logic operate?
        </p>
        <p className="leading-relaxed mb-6">
          Drafting by DMI provides system design and programming support to help integrators move from concept to control.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-10">
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-3">System design support can include:</h4>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li>Full low-voltage system design</li>
              <li>Line diagrams and system architecture layouts</li>
              <li>Equipment and infrastructure coordination</li>
              <li>Rack and wiring design</li>
              <li>Device placement and system flow validation</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-3">Programming Support for Lighting & Automation</h4>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li>Lutron lighting control setup and programming</li>
              <li>Savant programming support</li>
              <li>Lighting load schedules and systems layout</li>
              <li>Scene creation and logic structuring</li>
              <li>Keypad and control interface setup</li>
            </ul>
          </div>
        </div>

        <p className="leading-relaxed mb-6">
          Additional programming support can help integrators reduce on-site programming hours, prepare systems for final tuning, reduce errors before installation begins, and keep projects moving without overloading internal resources.
        </p>

        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Working With the Platforms Your Team Uses</h4>
        <p className="leading-relaxed mb-6">
          The supplied capabilities material identifies experience across platforms and products including Lutron, Savant, Ubiquiti, Araknis Networks, NETGEAR, AXIS Communications, ELK Products, Sony, Samsung, Sonance, Klipsch, Monitor Audio, Honeywell, and FloLogic, among others.
        </p>
        <p className="leading-relaxed mb-10">
          The value is not simply familiarity with individual brands. It is the ability to understand how equipment fits into the overall system and translate that understanding into usable design and programming documentation.
        </p>

        {/* ── Section: Takeoffs & Proposal Building ── */}
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-6">Takeoffs & Proposal Building</h3>

        <figure className="my-10 bg-gray-50 dark:bg-gray-800/50 p-6 rounded-2xl flex justify-center">
          <img src={takeoffFlyer} alt="Takeoffs & Proposal Flyer" className="max-h-96 w-auto object-contain rounded-xl shadow-sm" />
        </figure>

        <p className="leading-relaxed mb-6">
          For many low-voltage integrators, estimating and proposal preparation can consume significant amounts of engineering and management time. Plans need to be reviewed. Quantities need to be identified. Systems need to be broken down. Products need to be organized. Labor needs to be considered. The proposal needs to communicate the scope clearly.
        </p>
        <p className="leading-relaxed mb-6">
          Drafting by DMI provides takeoff and proposal support to help integrators turn plans and project assumptions into organized, bid-ready packages.
        </p>

        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Quantity Takeoffs</h4>
        <p className="leading-relaxed mb-6">
          DMI's takeoff support includes detailed quantity takeoffs from plan sets and low-voltage system breakdowns covering AV, security, networking, and lighting. This allows project teams to move from drawings and scope information toward a structured understanding of what the project requires.
        </p>

        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Proposal & Bid Package Support</h4>
        <ul className="list-disc pl-5 space-y-2 mb-8">
          <li>Full proposal and bid package assembly</li>
          <li>Scope-of-work documentation</li>
          <li>Labor-hour estimating support</li>
          <li>Organized product and system breakdowns</li>
          <li>Client-ready proposal formatting</li>
          <li>Revision support as project scope evolves</li>
        </ul>

        <div className="flex flex-wrap items-center gap-3 text-[#0d9488] font-medium my-10 bg-gray-50 dark:bg-gray-800/50 p-6 rounded-2xl">
          <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm">Plans</span>
          <i className="ri-arrow-right-line"></i>
          <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm">Takeoff</span>
          <i className="ri-arrow-right-line"></i>
          <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm">Proposal</span>
          <i className="ri-arrow-right-line"></i>
          <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm">Submission</span>
        </div>

        <hr className="border-gray-200 dark:border-gray-800 my-10" />

        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">What This Means for Your Team</h3>
        <p className="leading-relaxed mb-6">
          The value of outsourcing design and estimating support isn't simply about getting individual tasks completed. It's about creating capacity.
        </p>
        <p className="leading-relaxed mb-6">
          Your engineers can spend more time on engineering. Your project managers can spend more time managing projects. Your sales team can pursue opportunities instead of spending excessive time assembling proposals. Your installation team can receive clearer documentation.
        </p>
        <p className="leading-relaxed mb-10 text-xl font-light text-[#e18033]">
          And your company can take on additional work without immediately adding full-time headcount for every specialized function.
        </p>

        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Scale Without Hiring for Every Need</h3>
        <p className="leading-relaxed mb-6">
          Every integrator has periods of higher demand. Sometimes you need additional drafting capacity. Sometimes a bid deadline requires additional estimating support. Sometimes several projects reach the programming stage at the same time. Drafting by DMI can act as an extension of your team, providing support when and where it is needed.
        </p>

        <ul className="space-y-4 mb-10 pl-2">
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Need drafting capacity?</strong> We can help produce drawing packages.</span></li>
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Need system design support?</strong> We can help develop layouts, diagrams, infrastructure, and system architecture.</span></li>
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Need programming help?</strong> We can support lighting and automation programming requirements.</span></li>
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Need to get a bid out the door?</strong> We can help with takeoffs, BOMs, estimating, and proposal packages.</span></li>
        </ul>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Built Around the Way Integrators Work</h3>
        <p className="leading-relaxed mb-10">
          Every integrator has its own processes, preferred manufacturers, project standards, and customer expectations. The support should fit into the integrator's existing workflow. Whether you need help with a single project or ongoing production support, the objective is to become an extension of your team—not another layer of complexity.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">The Value of a Strong Design Partner</h3>
        <p className="leading-relaxed mb-4">
          A low-voltage integrator's value isn't limited to the equipment installed at the end of a project. Clear drawings can help communicate system intent. Accurate takeoffs can help establish project scope. Organized proposals can make bids easier to understand. Coordinated system designs can help installers work from a common reference. Structured programming can help move systems toward final commissioning.
        </p>
        <p className="leading-relaxed mb-12">
          These pieces work together. When they are handled efficiently, your internal team has more time to focus on the work that requires their direct attention.
        </p>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Let's Work Together</h2>
          <p className="mb-8 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Whether you need support for one upcoming project or are looking for an ongoing extension of your engineering and estimating team, Drafting by DMI can help.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <span className="bg-white dark:bg-gray-700 px-3 py-1 rounded-md text-sm shadow-sm border border-gray-100 dark:border-gray-600">Drafting & Design</span>
            <span className="bg-white dark:bg-gray-700 px-3 py-1 rounded-md text-sm shadow-sm border border-gray-100 dark:border-gray-600">System Design</span>
            <span className="bg-white dark:bg-gray-700 px-3 py-1 rounded-md text-sm shadow-sm border border-gray-100 dark:border-gray-600">Programming</span>
            <span className="bg-white dark:bg-gray-700 px-3 py-1 rounded-md text-sm shadow-sm border border-gray-100 dark:border-gray-600">Takeoffs</span>
            <span className="bg-white dark:bg-gray-700 px-3 py-1 rounded-md text-sm shadow-sm border border-gray-100 dark:border-gray-600">Estimating</span>
            <span className="bg-white dark:bg-gray-700 px-3 py-1 rounded-md text-sm shadow-sm border border-gray-100 dark:border-gray-600">Proposal Building</span>
          </div>

          <p className="text-[#0d9488] font-medium mb-8">
            We're currently offering a complimentary takeoff or design request for qualified companies exploring our services.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href="mailto:drafting@digitalmediainnovations.com" className="flex items-center text-gray-700 dark:text-gray-300 hover:text-[#0d9488] transition-colors">
              <i className="ri-mail-line mr-2 text-xl"></i>
              drafting@digitalmediainnovations.com
            </a>
            <a href="tel:3038731100" className="flex items-center text-gray-700 dark:text-gray-300 hover:text-[#0d9488] transition-colors">
              <i className="ri-phone-line mr-2 text-xl"></i>
              (303) 873-1100
            </a>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: '2',
    slug: 'comprehensive-low-voltage-plan-modern-smart-home',
    title: 'Comprehensive Low Voltage Plan for a Modern Smart Home',
    excerpt: 'A detailed look at how structured technology planning can transform a house into a connected, secure, and entertainment-focused smart home environment.',
    author: 'DMI AV Design Team',
    date: 'September 27, 2026',
    coverImage: systemDesignFlyer,
    metaTitle: 'Comprehensive Low Voltage Plan for a Modern Smart Home',
    metaDescription: 'Discover the importance of a comprehensive low voltage plan for modern smart homes. Learn how to coordinate networking, audio, TV, security, and smart control.',
    content: (
      <div className="space-y-8 text-gray-800 dark:text-gray-200">
        <p className="text-xl leading-relaxed text-gray-600 dark:text-gray-300 font-medium">
          Designing a Connected, Secure, and Entertainment-Focused Home
        </p>

        <p className="leading-relaxed">
          A well-planned low voltage system is the foundation of a modern smart home. From high-performance networking and whole-home audio to surveillance, televisions, motorized shades, and smart-home control, every system depends on careful planning, appropriate cabling, and proper equipment placement.
        </p>

        <p className="leading-relaxed">
          For this residential project, our team developed a comprehensive Low Voltage Plan designed to integrate multiple technology systems throughout the home. The plan covers the first floor, second floor, and key entertainment and outdoor areas, creating a structured infrastructure for today's technology while allowing flexibility for future upgrades.
        </p>

        <hr className="border-gray-200 dark:border-gray-800 my-10" />

        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mt-12 mb-6">A Complete Low Voltage Infrastructure</h3>

        <p className="leading-relaxed mb-6">
          The low voltage design brings together several technology systems into one coordinated plan, including:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
            <li>Home networking and Wi-Fi</li>
            <li>Television and entertainment systems</li>
            <li>Distributed audio</li>
            <li>Home theater / projector system</li>
            <li>Security cameras</li>
          </ul>
          <ul className="list-disc pl-5 space-y-2 text-gray-700 dark:text-gray-300">
            <li>Smart-home touchscreens</li>
            <li>Motorized roller shades</li>
            <li>Structured CAT6 cabling</li>
            <li>Audio amplifier and AVR connections</li>
            <li>Outdoor entertainment</li>
          </ul>
        </div>

        <p className="leading-relaxed font-medium mb-10 border-l-4 border-[#0d9488] pl-4 italic bg-gray-50 dark:bg-gray-800/30 py-3 rounded-r-lg">
          By coordinating these systems during the planning stage, the installation can be completed with organized cabling and clearly defined equipment locations.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-4">Structured Network and CAT6 Cabling</h3>
        <p className="leading-relaxed mb-6">
          Reliable connectivity is essential for a modern home. The plan includes CAT6 cabling routed back to the network rack for televisions, wireless access points, and other connected devices.
        </p>
        <p className="leading-relaxed mb-6">
          Multiple TV locations are provided with three CAT6 cables to the network rack, while selected devices such as wireless access points and other network-connected equipment are provided with dedicated CAT6 connections.
        </p>
        <p className="leading-relaxed mb-6">
          The design also includes <strong>Ubiquiti U7 Pro Max</strong> / <strong>U7 Pro</strong> outdoor wireless access points, providing a structured approach to whole-home wireless connectivity.
        </p>
        <p className="leading-relaxed mb-10">
          This centralized network infrastructure allows the home to support high-bandwidth entertainment, smart-home devices, security systems, and everyday internet-connected equipment.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-4">Whole-Home Audio</h3>
        <p className="leading-relaxed mb-6">
          Audio is an important part of the low voltage design. The plan incorporates a combination of Sonance in-ceiling and in-wall speakers, along with outdoor speakers for exterior entertainment areas.
        </p>
        <p className="leading-relaxed mb-4">The specified equipment includes:</p>
        <ul className="list-disc pl-5 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
          <li>Sonance VX66R / VX64R 6-inch round two-way in-ceiling speakers</li>
          <li>Sonance VXXT6R 6-inch in-ceiling speakers</li>
          <li>Sonance R1SUR 5.25-inch in-wall speakers</li>
          <li>Sonance MARINER MX66 outdoor speakers</li>
          <li>Sonance SGS 8.1 systems with DSP 2-150 MKIII</li>
          <li>James Loudspeaker M122 12-inch dual subwoofer</li>
        </ul>
        <p className="leading-relaxed mb-10">
          Speaker locations and amplifier/AVR wiring are coordinated within the plan to provide the required infrastructure for distributed audio throughout the residence.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-4">Television and Entertainment Systems</h3>
        <p className="leading-relaxed mb-6">
          The plan provides dedicated infrastructure for multiple television locations throughout the home. The project includes several TV applications, such as 55-inch, 65-inch, and 85-inch televisions, a Gym TV, Recreation room TV, Primary bedroom TV, and an Outdoor television (including a <strong>Seura 65-inch TV Mirror</strong> and a <strong>Samsung 65-inch The Terrace</strong>).
        </p>
        <p className="leading-relaxed mb-6">
          TV locations are supported by CAT6 connections to the network rack, while dedicated 14/4 speaker wiring is provided to amplifiers or AV receivers where required.
        </p>
        <p className="leading-relaxed mb-10">
          This approach creates a clean and organized infrastructure behind each entertainment location while keeping the main equipment centralized.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-4">Dedicated Home Theater and Projection System</h3>
        <p className="leading-relaxed mb-6">
          A dedicated entertainment area is incorporated into the low voltage design with a projector and <strong>165-inch screen</strong>.
        </p>
        <p className="leading-relaxed mb-6">
          The plan also provides the associated speaker and subwoofer infrastructure required for an immersive entertainment environment. Dedicated 14/4 wiring is routed to amplifiers/AV receivers, while CAT6 connectivity is provided back to the network rack.
        </p>
        <p className="leading-relaxed mb-10">
          This centralized approach simplifies equipment management while providing the infrastructure needed for a high-performance home theater environment.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 my-10">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Smart Home Control</h3>
            <p className="leading-relaxed mb-4">
              The residence includes <strong>Control4 C4-T4IW10-WH T4 In-Wall Touchscreens</strong> at designated locations. These touchscreens provide a centralized interface for controlling connected home systems.
            </p>
            <p className="leading-relaxed">
              Their inclusion in the low voltage plan ensures that appropriate network connectivity and installation provisions are planned in advance, providing a consistent user interface for the home's technology systems.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Motorized Roller Shades</h3>
            <p className="leading-relaxed mb-4">
              Motorized shade infrastructure is carefully incorporated into the design. The plan identifies roller shade locations and provides connections from the shade locations to the <strong>Lutron SP-LUT1-500 shade panel</strong>.
            </p>
            <p className="leading-relaxed">
              Planning the shade wiring during the construction stage helps ensure that motorized shades can be installed cleanly without requiring exposed wiring or major modifications later.
            </p>
          </div>
        </div>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-4">Security Camera Infrastructure</h3>
        <p className="leading-relaxed mb-10">
          Security is another important component of the low voltage design. The plan identifies <strong>G6 Bullet Pro</strong> cameras distributed around the property to provide comprehensive coverage. Dedicated CAT6 connections are routed back to the network rack, supporting the installation of IP-based surveillance while keeping the network infrastructure meticulously organized.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-4">Outdoor Connectivity and Entertainment</h3>
        <p className="leading-relaxed mb-4">
          The low voltage plan extends well beyond the interior of the residence. Outdoor technology provisions include:
        </p>
        <ul className="list-disc pl-5 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
          <li>Sonance MARINER MX66 outdoor speakers</li>
          <li>Outdoor television</li>
          <li>G6 Bullet Pro security cameras</li>
          <li>U7 Pro / U7 Pro Max outdoor wireless access points</li>
          <li>Motorized shade infrastructure</li>
        </ul>
        <p className="leading-relaxed mb-10">
          This comprehensive approach allows outdoor areas to remain fully connected to the home's entertainment, network, and security systems.
        </p>

        <hr className="border-gray-200 dark:border-gray-800 my-10" />

        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Floor-by-Floor Planning</h3>

        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">First Floor</h4>
        <p className="leading-relaxed mb-6">
          The first-floor low voltage plan integrates television locations, distributed audio, networking, surveillance, wireless access points, smart-home control, and motorized shades. It features Sonance VX64R and VXXT6R speakers, outdoor MARINER MX66 speakers, multiple CAT6 connections to the network rack, and 14/4 speaker wiring to amplifiers. A Control4 T4 In-Wall Touchscreen is also included, along with dedicated infrastructure for televisions and outdoor technology.
        </p>

        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Second Floor</h4>
        <p className="leading-relaxed mb-10">
          The second-floor plan focuses on bedrooms and associated living spaces. The Primary Bedroom includes a TV location, Sonance VX64R in-ceiling speakers, CAT6 connectivity to the network rack, and motorized shade infrastructure. Multiple roller shade locations are connected to the Lutron shade panel, while U7 Pro Max wireless access points provide robust network infrastructure throughout the floor.
        </p>

        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">A Coordinated Approach to Low Voltage Design</h3>
        <p className="leading-relaxed mb-6">
          The key objective of this low voltage plan is to ensure that all technology systems are considered together rather than designed independently. Network cabling, audio wiring, television connections, surveillance cameras, wireless access points, motorized shades, and control devices are all coordinated within the same infrastructure.
        </p>

        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Practical Benefits of Coordination:</h4>
        <ul className="space-y-4 mb-10 pl-2">
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Better organization:</strong> Centralized network and AV connections make future service and maintenance easier.</span></li>
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Improved installation:</strong> Cabling routes and equipment locations are identified before installation begins.</span></li>
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Scalability:</strong> The structured infrastructure provides flexibility for future technology upgrades.</span></li>
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>Cleaner finished spaces:</strong> Proper planning reduces visible wiring and helps maintain a clean architectural appearance.</span></li>
          <li className="flex items-start"><i className="ri-check-line text-[#0d9488] text-xl mr-3"></i> <span><strong>System integration:</strong> Different technology systems can operate seamlessly as part of a coordinated smart-home environment.</span></li>
        </ul>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 border border-gray-100 dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Conclusion</h2>
          <p className="mb-4 text-gray-700 dark:text-gray-300">
            This residential low voltage plan demonstrates how structured technology planning can transform a house into a connected smart-home environment. By integrating networking, distributed audio, home theater, televisions, surveillance, wireless connectivity, motorized shades, and smart-home control into one coordinated infrastructure, the project establishes a reliable foundation for modern residential technology.
          </p>
          <p className="text-gray-700 dark:text-gray-300 font-medium italic">
            A detailed low voltage plan is more than a drawing—it is the roadmap that ensures every device has the appropriate location, connectivity, and infrastructure before installation begins. Our approach focuses on creating technology systems that are organized, scalable, and integrated with the overall design of the home.
          </p>
        </div>

      </div>
    ),
  },
  {
    id: '3',
    slug: 'upgrading-legacy-lutron-homeworks-illumination-systems',
    title: 'Upgrading Legacy Lutron HomeWorks Illumination Systems',
    excerpt: 'Legacy Lutron HomeWorks Illumination systems can become challenging to maintain. Upgrading to the modern HomeWorks QSX platform requires proper planning and drafting for a smooth transition.',
    author: 'DMI Team',
    date: 'October 5, 2026',
    coverImage: blogCoverImage,
    metaTitle: 'Upgrading Legacy Lutron HomeWorks | Drafting by DMI',
    metaDescription: 'Proper planning and device compatibility verification help ensure a smooth transition to Lutron HomeWorks QSX with minimal disruption.',
    content: (
      <div className="space-y-8 text-gray-800 dark:text-gray-200">
        <p className="text-xl leading-relaxed text-gray-600 dark:text-gray-300 font-medium">
          Legacy Lutron HomeWorks Illumination systems can become challenging to maintain as components age and replacement parts become harder to source.
        </p>

        <p className="leading-relaxed">
          Upgrading to the modern HomeWorks QSX platform provides improved control flexibility, updated keypads, dimmers, and switches, and a solid foundation for future smart home integration. However, a successful transition requires more than just swapping out hardware—it demands meticulous planning and documentation.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-6">The Role of Drafting in System Upgrades</h3>

        <p className="leading-relaxed mb-6">
          When transitioning from legacy Illumination to QSX, relying on outdated or incomplete as-built drawings can lead to costly delays and installation errors. At Drafting by DMI, we focus on providing the critical documentation needed before the first wire is touched.
        </p>

        <ul className="list-disc pl-5 space-y-2 mb-8">
          <li><strong>Device Compatibility Verification:</strong> We map out existing keypads, dimmers, and modules to cross-reference compatibility with QSX requirements, ensuring no surprises during the upgrade.</li>
          <li><strong>Panel Schedules and Load Planning:</strong> We create detailed panel schedules that accurately reflect the new system's load distribution and circuit requirements, minimizing disruption to existing lighting circuits.</li>
          <li><strong>Wiring Schematics and Layouts:</strong> We provide updated wiring schematics and floor plan layouts that guide integrators clearly on what stays, what goes, and what needs to be retrofitted.</li>
        </ul>

        <blockquote className="border-l-4 border-[#0d9488] pl-6 py-2 my-10 italic text-xl text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800/30 rounded-r-xl">
          Proper planning and device compatibility verification help ensure a smooth transition with minimal disruption to existing lighting circuits.
        </blockquote>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-12 mb-6">Why Accurate Documentation Matters</h3>

        <p className="leading-relaxed mb-6">
          Upgrading an entire home's lighting control system is a complex logistical task. Without clear, updated drafting and design documents, installation teams are left to guess how legacy components were originally wired. Our drafting services bridge the gap between the old system's architecture and the new QSX platform's requirements.
        </p>

        <p className="leading-relaxed mb-6">
          By preparing comprehensive line diagrams, panel elevations, and device location maps, we equip low-voltage integrators with the exact blueprints they need to execute the upgrade efficiently and correctly the first time.
        </p>

        <hr className="border-gray-200 dark:border-gray-800 my-10" />

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 border border-gray-100 dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Conclusion</h2>
          <p className="mb-4 text-gray-700 dark:text-gray-300">
            While the physical installation of a Lutron HomeWorks QSX system is critical, the success of the upgrade is heavily dependent on the engineering and drafting work done beforehand. By investing in accurate, detailed documentation, you provide a clear roadmap for integrators and ensure a seamless transition for the homeowner.
          </p>
        </div>
      </div>
    ),
  }
].sort((a, b) => Number(b.id) - Number(a.id));
